package com.eladfinish.bible.contest.analytics.providers

/**
 * Adapts an app analytics event to Firebase Analytics' naming rules, which silently drop
 * (as an `_err` event) any event whose name or param key isn't 1-40 ASCII letters, digits or
 * underscores starting with a letter, and truncate string values past 100 characters.
 *
 * Many app event names are free text (Hebrew button labels, "HebrewGender - first launch"),
 * and AppMetrica accepts them as-is, so they're only rewritten on the way to Firebase:
 * - names that are ASCII apart from separators become snake_case ("random verse settings" →
 *   "random_verse_settings");
 * - anything else (e.g. Hebrew) is sent as [GENERIC_EVENT_NAME] with the original name in the
 *   [ORIGINAL_NAME_PARAM] param, so it's still countable and filterable in Firebase.
 * Params whose key can't be made valid are dropped.
 */
internal data class FirebaseEvent(val name: String, val params: Map<String, Any>?) {

    companion object {
        const val GENERIC_EVENT_NAME = "app_action"
        const val ORIGINAL_NAME_PARAM = "action_name"

        private const val MAX_NAME_LENGTH = 40
        private const val MAX_STRING_VALUE_LENGTH = 100
        private val VALID_NAME = Regex("[A-Za-z][A-Za-z0-9_]{0,${MAX_NAME_LENGTH - 1}}")
        private val SEPARATORS = Regex("[\\s\\-.:/]+")
        private val RESERVED_PREFIXES = listOf("firebase_", "google_", "ga_")

        fun from(event: String, params: Map<String, Any>?): FirebaseEvent {
            val safeParams = params?.entries
                ?.mapNotNull { (key, value) -> toValidName(key)?.let { it to value.toParamValue() } }
                ?.toMap()
            val name = toValidName(event)
                ?: return FirebaseEvent(
                    name = GENERIC_EVENT_NAME,
                    params = safeParams.orEmpty() + (ORIGINAL_NAME_PARAM to event.toParamValue()),
                )
            return FirebaseEvent(name = name, params = safeParams)
        }

        /** [text] as-is if valid, else its snake_case form if that is valid, else null. */
        private fun toValidName(text: String): String? {
            if (text.isValidName()) return text
            val snakeCase = text.trim().replace(SEPARATORS, "_").trim('_')
            return snakeCase.takeIf { it.isValidName() }
        }

        private fun String.isValidName(): Boolean =
            VALID_NAME.matches(this) && RESERVED_PREFIXES.none { startsWith(it, ignoreCase = true) }

        /** Numbers pass through; everything else (enums, booleans, ...) is sent as text. */
        private fun Any.toParamValue(): Any =
            if (this is Number) this else toString().take(MAX_STRING_VALUE_LENGTH)
    }
}
